import { useState } from 'react'
import { Button, Select, Space } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Can } from '@/shared/auth/Can'
import { PageHeader } from '@/shared/ui/PageHeader'
import { CashBranchSelect } from '@/features/cashdesk/CashBranchSelect'
import { useCashBranches } from '@/features/cashdesk/useCashBranches'
import { INVOICE_STATES, type InvoiceState } from '@/features/invoice/invoiceApi'
import { InvoiceFormModal } from '@/features/invoice/InvoiceFormModal'
import { InvoiceTable } from '@/features/invoice/InvoiceTable'

export function InvoicesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const branches = useCashBranches()
  const [state, setState] = useState<InvoiceState | null>(null)
  const [branchId, setBranchId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  return (
    <>
      <PageHeader
        title={t('nav.invoices')}
        description={t('pageIntro.invoices')}
        extra={
          <Can permission="invoice.create">
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
              {t('invoices.create')}
            </Button>
          </Can>
        }
      />

      <InvoiceTable
        filter={{ state, branchId }}
        tableId="invoices"
        toolbarExtra={
          <Space wrap>
            {branches.all.length > 1 ? (
              <CashBranchSelect
                branches={branches.all}
                value={branchId}
                onChange={setBranchId}
                loading={branches.isPending}
                allowClear
                placeholder={t('cashdesk.allBranches')}
              />
            ) : null}
            <Select<string>
              style={{ minWidth: 170 }}
              value={state ?? 'ALL'}
              onChange={(value) => setState(value === 'ALL' ? null : (value as InvoiceState))}
              options={[
                { value: 'ALL', label: t('cashdesk.allStates') },
                ...INVOICE_STATES.map((s) => ({ value: s, label: t(`invoices.state.${s}`) })),
              ]}
            />
          </Space>
        }
      />

      <InvoiceFormModal
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={(invoice) => {
          setCreating(false)
          void navigate(`/invoices/${invoice.id}`)
        }}
      />
    </>
  )
}
